import { RevoluteJoint, WeldJoint } from "fast-2d";
import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";
import { makeWorld, floor, walls, box, ball, rng } from "./helpers";

/**
 * Motorized revolute joints in action: a row of powered paddle-wheels churns a
 * pile of balls, each driven at a fixed speed with a capped torque. You can
 * still grab a paddle or a ball and fight the motor.
 */
export const machine: Demo = {
	id: "machine",
	title: "Motors",
	blurb:
		"Powered hinges. Each paddle-wheel is a revolute joint with the motor enabled, spinning at a fixed speed with a capped torque while it churns a pile of balls.",
	category: "Joints & Machines",
	build(): Scene {
		const world = makeWorld();
		floor(world, 26);
		walls(world, 11, 16);

		// A row of counter-rotating paddle-wheels, each pinned to a static hub.
		const paddleLen = 2.6;
		const hubs = [
			{ x: -6, y: -3, speed: 3.5 },
			{ x: 0, y: -4.5, speed: -4 },
			{ x: 6, y: -3, speed: 3.5 },
		];
		for (const h of hubs) {
			// Two crossed bars make a four-armed paddle.
			const barA = box(world, h.x, h.y, paddleLen, 0.35, { density: 1.5 });
			const barB = box(world, h.x, h.y, 0.35, paddleLen, { density: 1.5 });
			// Weld the second bar onto the first so they spin as one rigid cross,
			// then drive that cross with a single motorized hinge pinned to a
			// fixed world point (no hub body for the balls to snag on).
			world.addConstraint(
				new WeldJoint(barA, barB, { localAnchorA: [0, 0], localAnchorB: [0, 0] }),
			);
			world.addConstraint(
				new RevoluteJoint(barA, null, {
					localAnchorA: [0, 0],
					localAnchorB: [h.x, h.y],
					enableMotor: true,
					motorSpeed: h.speed,
					maxMotorTorque: 4000,
				}),
			);
		}

		// A pile of balls for the paddles to fling around.
		const rand = rng(2027);
		for (let i = 0; i < 28; i++) {
			ball(world, -8 + rand() * 16, -12 - rand() * 4, 0.32 + rand() * 0.18, {
				density: 0.8,
				restitution: 0.25,
				friction: 0.4,
			});
		}

		return {
			world,
			camera: new Camera(0, -5, 24),
			hint: "Grab a paddle or a ball and fight the motor",
		};
	},
};
